<script>
import {clientProduct as columns} from '../../../tables'

export default {
  name: "client-product",
  props: {
    client: {
      type: String,
      required: true
    }
  },
  computed: {
    pageSize() {
      return parseInt(this.clientProducts.per_page)
    },
  },
  data() {
    return {
      columns,
      addId: 99999999,
      editingId: 0,
      loading: true,
      clientProducts: {},
      cachedClientProducts: [],
      filters: {
        page: 1,
        per_page: 10
      },
      stateChanging: []
    }
  },
  methods: {
    getClientProducts() {
      this.$http.get(this.routeUri('hyperion.client.product', {
        client_hash: this.client
      }), { params: this.filters })
        .then(res => {
          this.clientProducts = res.data
          this.cachedClientProducts = this.clientProducts.data.map(item => ({...item}));
        }).catch(err => this.handleXhrError(err))
        .finally(() => this.loading = false)
    },
    edit(hash) {
      const newData = [...this.clientProducts.data];
      const target = newData.filter(item => hash === item.hash)[0];
      this.editingId = hash
      if (target) {
        target.editable = true;
        this.clientProducts.data = newData;
      }
    },
    cancel(hash) {
      let newData = [...this.clientProducts.data];
      const target = newData.filter(item => hash === item.hash)[0];
      this.editingId = 0;
      if (target) {
        if (target.hasOwnProperty('add')) {
          delete target.add
          newData.shift()
        } else {
          Object.assign(target, this.cachedClientProducts.filter(item => hash === item.hash)[0]);
          delete target.editable;
        }

        this.clientProducts.data = newData;
      }
    },
    append() {
      const products = this.clientProducts.data;

      const newProduct = {
        // Hardcoded ID
        hash: this.addId,
        code: "",
        name: "Select Product",
        group: {
          name: ""
        },
        disabled_at: null,
        price: 0,
        admin_fee: 0,
        share_fee: 0,
        add: true,
        pricing: {
          disabled_at: null
        }
      };
      products.unshift(newProduct);
      this.clientProducts.data = products;

      this.edit(this.addId)
    },
    onRowProductChanged(product, record) {
      record.id = product.id
      record.label = product.label
      record.hash = product.hash
      record.code = product.code
      record.name = product.name
      record.group = product.group
    },
    onInputChange(event, record, column) {
      record[column] = event.target.value
    },
    updateRow(product) {
      if (product.add) this.attach(product)
      else this.setPrice(product)
    },
    setPrice(product) {
      this.$http.put(this.routeUri('hyperion.client.product.update', {
        client_hash: this.client,
        product_hash: product.hash
      }), {
        price: product.price,
        admin_fee: product.admin_fee,
        share_fee: product.share_fee
      }).then(res => this.handleXhrRequest(res))
        .catch(err => this.handleXhrError(err))
        .finally(() => {})
    },
    attach(product) {
      this.$http.post(this.routeUri('hyperion.client.product.attach', {
        client_hash: this.client,
        product_hash: product.hash
      }), {
        price: product.price,
        admin_fee: product.admin_fee,
        share_fee: product.share_fee
      }).then(res => this.handleXhrRequest(res))
        .catch(err => this.handleXhrError(err))
        .finally(() => {})
    },
    detach(product) {
      this.$http.delete(this.routeUri('hyperion.client.product.detach', {
        client_hash: this.client,
        product_hash: product.hash
      })).then(res => this.handleXhrRequest(res))
        .catch(err => this.handleXhrError(err))
    },
    handleXhrRequest(res) {
      if (res.data.status) {
        this.$notification.success({
          message: 'Success!',
          description: 'Data has been successfully saved!'
        })
        setTimeout(() => window.location.href = res.data.redirect, 2000)
      }
    },
    tableChanged(pagination) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize
      this.pushState(this.filters)
      this.getClientProducts()
    },
    toggleState(record, current) {
      this.stateChanging.push(record.hash)
      this.$http.patch(this.routeUri('hyperion.client.product.patch', {
        client_hash: this.client,
        product_hash: record.hash
      }), {
        action: 'TOGGLE_STATE',
        current: current
      })
        .then(() => this.onSuccessChangingState(record, current))
        .catch((error) => this.handleXhrError(error))
        .finally(() => this.onFinishedChangingState(record))
    },
    onSuccessChangingState(record, current) {
      let index = this.clientProducts.data.findIndex(i => i.hash === record.hash)
      if (index > -1) {
        this.clientProducts.data[index].pricing.disabled_at = (current === null) ? 'mark' : null
      }
    },
    onFinishedChangingState(record) {
      let index = this.stateChanging.indexOf(record.hash)
      if (index > -1) {
        this.stateChanging.splice(index, 1)
      }
    }
  },
  created() {
    this.clientProducts = this.getDefaultPagination()
    this.filters = _.merge(this.filters, this.$hyperion.request)
    this.getClientProducts()
  }
}
</script>
<template>
  <a-card type="inner" size="small">
    <a slot="extra" href="javascript:void(0);" @click="append"><a-icon type="plus" /> Add Product</a>
    <a-skeleton :loading="loading" active>
      <a-table :data-source="clientProducts.data"
               :columns="columns"
               rowKey="hash"
               :pagination="{ pageSize: pageSize, current: clientProducts.current_page, total: clientProducts.total, showSizeChanger: true }"
               @change="tableChanged">

        <div slot="productGroup" slot-scope="record">
          <a-icon type="tags"></a-icon>
          {{ record.group.name }}
        </div>

        <div slot="productCode" slot-scope="code">
          <a-icon type="code"></a-icon>
          <code>{{ code }}</code>
        </div>

        <div slot="productName" slot-scope="name, record">
          <single-product-autocomplete v-if="record.editable && record.add" :current-value="record.name"
                                       @change="(product) => onRowProductChanged(product, record)"></single-product-autocomplete>
          <template v-else>
            <a-icon type="tag"></a-icon>
            {{ name }}
          </template>
        </div>

        <div slot="basePrice" slot-scope="price, record">
          <a-input
            v-if="record.editable"
            style="margin: -5px 0"
            :value="price"
            @change="e => onInputChange(e, record, 'price')"
          />
          <template v-else>
            <h4><currency :amount="price" /></h4>
          </template>
        </div>

        <div slot="adminFee" slot-scope="admin_fee, record">
          <a-input
            v-if="record.editable"
            style="margin: -5px 0"
            :value="admin_fee"
            @change="e => onInputChange(e, record, 'admin_fee')"
          />
          <template v-else>
            <h4><currency :amount="admin_fee" /></h4>
          </template>
        </div>

        <div slot="shareFee" slot-scope="share_fee, record">
          <a-input
            v-if="record.editable"
            style="margin: -5px 0"
            :value="share_fee"
            @change="e => onInputChange(e, record, 'share_fee')"
          />
          <template v-else>
            <h4><currency :amount="share_fee" /></h4>
          </template>
        </div>

        <div slot="status" slot-scope="record">
            <a-tooltip title="Base Product Status">
              <a-tag color="#87d068" v-if="record.disabled_at === null"><a-icon type="fork" /></a-tag>
              <a-tag color="#f50" v-else><a-icon type="fork" /></a-tag>
            </a-tooltip>

          <template v-if="stateChanging.includes(record.hash)">
            <a-tag>LOADING</a-tag>
          </template>
          <template v-else>
            <a-popconfirm title="Sure to disable product?" @confirm="() => toggleState(record, record.pricing.disabled_at)" v-if="record.pricing.disabled_at === null">
              <a-tag style="cursor:pointer;" color="#87d068">Active</a-tag>
            </a-popconfirm>
            <a-popconfirm title="Sure to enable product?" @confirm="() => toggleState(record, record.pricing.disabled_at)" v-else>
              <a-tag style="cursor:pointer;" color="#f50">Inactive</a-tag>
            </a-popconfirm>
          </template>
        </div>

        <div class="editable-row-operations" slot="action" slot-scope="record">
          <template v-if="record.editable">
            <a-button type="link" @click="() => updateRow(record)">Save</a-button>
            <a-popconfirm title="Sure to cancel?" @confirm="() => cancel(record.hash)">
              <a-button type="link">Cancel</a-button>
            </a-popconfirm>
          </template>
          <template v-else>
            <a-button :disabled="editingId !== 0" @click="() => edit(record.hash)" type="primary" icon="edit" size="small" />
            <a-popconfirm
              v-if="clientProducts.data.length"
              title="Sure to delete?"
              @confirm="() => detach(record)">
              <a-button :disabled="editingId !== 0" type="danger" size="small" icon="delete" />
            </a-popconfirm>
          </template>
        </div>

      </a-table>
    </a-skeleton>
  </a-card>
</template>

